import { storeToRefs } from "pinia"
import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router"

import { type Permission } from "@/types"
import { useSessionStore } from "@/store/session"

import { useConfigurationStore } from "./store/configuration"
import { useOnNextResult } from "./utils/misc"
import adminRoutes from "./views/admin/routes"
import childrenRoutes from "./views/children/routes"
import custodiansRoutes from "./views/custodians/routes"
import exportRoutes from "./views/export/routes"
import feeRoutes from "./views/fees/routes"
import analysisRoutes from "./views/home/routes"
import employeesRoutes from "./views/personal/routes"
import stammdatenRoutes from "./views/stammdaten/routes"

export const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "Home",
    component: () => import("./views/base/AppLayout.vue"),
    children: [
      {
        path: "/",
        name: "dashboard",
        meta: {
          requiresInstitutionContext: false,
          requiresCustomerContext: false,
        },
        component: () => import("./views/DashBoard.vue"),
        children: [
          {
            path: "erstellen",
            name: "DashboardAppointmentDialogCreate",
            component: () => import("./views/personal/appointments/AppointmentDialog.vue"),
          },
          {
            path: "dashboard/termine/:appointmentId",
            name: "DashboardAppointmentDialogEdit",
            component: () => import("./views/personal/appointments/AppointmentDialog.vue"),
          },
        ],
      },
      {
        path: "/versionshinweise",
        name: "Changelog",
        component: () => import("./views/changelog/Changelog.vue"),
      },
      {
        path: "/:pathMatch(.*)*",
        name: "PageNotFound",
        component: () => import("./views/error/NotFound.vue"),
      },
      ...childrenRoutes,
      ...custodiansRoutes,
      ...employeesRoutes,
      ...stammdatenRoutes,
      ...adminRoutes,
      ...feeRoutes,
      ...analysisRoutes,
      ...exportRoutes,
    ],
  },
  {
    path: "/report/:resource?",
    name: "report",
    component: () => import("./views/PsPdf.vue"),
    props: true,
    meta: {
      requiresAuth: true,
      requiresInstitutionContext: true,
    },
  },
  {
    path: "/login",
    name: "login",
    component: () => import("./views/LoginPage.vue"),
    meta: {
      requiresAuth: false,
      requiresInstitutionContext: false,
      requiresCustomerContext: false,
    },
  },
  {
    path: "/error",
    name: "error",
    component: () => import("./views/error/ErrorPage.vue"),
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
})

router.beforeEach(async (to) => {
  const sessionStore = useSessionStore()

  const requiresAuth = to.meta?.requiresAuth !== false

  if (requiresAuth) {
    // try login w cached token
    if (!sessionStore.loggedIn) {
      await sessionStore.logBackIn()
    }
    // if still not logged in, forward to keycloak login
    if (!sessionStore.loggedIn) {
      await sessionStore.login()
    }
  }

  const configStore = useConfigurationStore()

  const missingPageContext =
    (to.meta?.requiresCustomerContext !== false && !sessionStore.contextCustomerId) ||
    (to.meta?.requiresInstitutionContext !== false && !sessionStore.contextInstitutionId)

  if (missingPageContext) {
    return {
      name: "dashboard",
    }
  }

  const requiresConfiguration = to.meta?.requiresConfiguration ?? []

  if (configStore.configurationQueryResult == null) {
    await useOnNextResult(storeToRefs(configStore).configurationQueryResult)
  }

  const missingConfiguration = !requiresConfiguration.every(
    (key) => configStore.configuration?.[key] === true
  ) // only config boolean types

  // We don't have a specific role to grant users in order to access the migration page.
  // We rely on access levels instead of roles
  const missingCustomerOrHigher =
    to.meta?.requiresCustomerOrHigher && !sessionStore.isUserCustomerOrHigher

  const missingAccessLevel = missingCustomerOrHigher //||

  const missingRole =
    (((to.meta?.requiredRoles as Permission[]) || []).length > 0 &&
      !sessionStore.hasRoles(<Permission[]>to.meta?.requiredRoles)) ||
    (to.meta?.requiresAdmin && !sessionStore.isUserAdmin)

  if (missingRole || missingAccessLevel || missingConfiguration) {
    return { name: "error" }
  }
})
